<template>
  <section id="dashboard">
    <div v-if="userData.roles[0].id == 1">
      <superAdmin
        :isLoading="isLoading"
        :userData="userData"
        :dataDashboard="dataDashboard"
        @buscar="buscar($event)"
        @export-csv="exportDashboardCsv"
        @export-excel="exportDashboardExcel"
      />
    </div>
    <div v-if="userData.roles[0].id == 2">
      <admin
        :isLoading="isLoading"
        :userData="userData"
        :dataDashboard="dataDashboard"
        @buscar="buscar($event)"
        @export-csv="exportDashboardCsv"
        @export-excel="exportDashboardExcel"
      />
    </div>
    <div v-if="userData.roles[0].id == 3">
      <oma :isLoading="isLoading" :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 4">
      <nurse :isLoading="isLoading" :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>
    <div v-if="userData.roles[0].id == 5">
      <frontdesk :isLoading="isLoading" :userData="userData" :dataDashboard="dataDashboard" @buscar="buscar($event)" />
    </div>

    <ProgressiveLoader
      :showLoader="showProgressiveLoader"
      :currentProgress="progressiveProgress.current"
      :totalMonths="progressiveProgress.total"
      :currentMonth="progressiveProgress.currentMonth"
      :currentYear="progressiveProgress.currentYear"
      :allowCancel="true"
      @cancel="cancelProgressiveLoading"
    />

    <ProgressiveNotification
      :showNotification="showProgressiveNotification"
      @close="showProgressiveNotification = false"
    />
  </section>
</template>

<script>
import axios from "@/core/services/apiInsurance/dashboard";
import {
  generateBlocks,
  loadProgressiveData,
} from "@/core/services/apiInsurance/dashboard/progressiveLoader";
import { daysBetween } from "@/core/utils/dashboardDates";
import ProgressiveLoader from "@/view/pages/dashboard/components/ProgressiveLoader.vue";
import ProgressiveNotification from "@/view/pages/dashboard/components/ProgressiveNotification.vue";
import superAdmin from "./superAdmin/superAdmin.vue";
import admin from "./admin/admin.vue";
import frontdesk from "./frontdesk/frontdesk.vue";
import nurse from "./nurse/nurse.vue";
import oma from "./oma/oma.vue";
import {
  exportDashboardToCsv,
  exportDashboardToExcel,
} from "@/view/pages/dashboard-all/utils/dashboardExport";

export default {
  components: {
    admin,
    frontdesk,
    nurse,
    oma,
    superAdmin,
    ProgressiveLoader,
    ProgressiveNotification,
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData")),
      dataDashboard: {},
      isLoading: false,
      hasSearchResults: false,
      lastSearchContext: null,
      showProgressiveLoader: false,
      showProgressiveNotification: false,
      progressiveProgress: {
        current: 0,
        total: 0,
        currentMonth: 0,
        currentYear: 0,
      },
      progressiveLoadingController: null,
    };
  },
  computed: {
    canExport() {
      return (
        this.hasSearchResults &&
        !!this.lastSearchContext &&
        this.dataDashboard &&
        Object.keys(this.dataDashboard).length > 0
      );
    },
  },
  methods: {
    async buscar({ start, end, facilities }) {
      if (this.progressiveLoadingController) {
        this.progressiveLoadingController.abort();
      }
      const controller = new AbortController();
      this.progressiveLoadingController = controller;

      this.isLoading = true;
      this.lastSearchContext = { start, end, facilities };

      const userId =
        this.userData.roles[0].id != 1 && this.userData.roles[0].id != 2
          ? this.userData.id
          : null;

      const blockCount = generateBlocks(start, end).length;
      const useProgressive = daysBetween(start, end) > 15 && !this.isMobileDevice();
      const showModal = useProgressive && blockCount > 1;

      try {
        let data;
        if (useProgressive) {
          if (showModal) {
            this.progressiveProgress = {
              current: 0,
              total: blockCount,
              currentMonth: 0,
              currentYear: 0,
            };
            this.showProgressiveLoader = true;
            this.showProgressiveNotification = true;
          }

          data = await loadProgressiveData(
            { start, end, facilities },
            {
              userId,
              signal: controller.signal,
              onProgress: (progress) => {
                if (showModal && this.progressiveLoadingController === controller) {
                  this.progressiveProgress = progress;
                }
              },
            }
          );
        } else {
          const datarda = { start_date: start, end_date: end, facilities };
          if (userId) datarda.user_id = userId;
          const response = await axios.getData(datarda, { signal: controller.signal });
          data = response?.data ?? response;
          if (!data) throw new Error("Empty dashboard response");
        }

        if (this.progressiveLoadingController !== controller) return;
        this.dataDashboard = data;
        this.hasSearchResults = true;
      } catch (error) {
        if (error?.name === "AbortError" || this.progressiveLoadingController !== controller) {
          return;
        }
        console.error("Error en búsqueda (insurance):", error);
        this.$bvToast.toast(
          error?.response?.data?.message || "Failed to load data. Please try again.",
          {
            title: "Load error",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
      } finally {
        if (this.progressiveLoadingController === controller) {
          this.progressiveLoadingController = null;
          this.isLoading = false;
          this.showProgressiveLoader = false;
          this.showProgressiveNotification = false;
        }
      }
    },

    isMobileDevice() {
      const isMobileByScreen = window.innerWidth <= 768;
      const isMobileByAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      return isMobileByScreen || isMobileByAgent;
    },

    cancelProgressiveLoading() {
      if (this.progressiveLoadingController) {
        this.progressiveLoadingController.abort();
        this.progressiveLoadingController = null;
      }
      this.isLoading = false;
      this.showProgressiveLoader = false;
      this.showProgressiveNotification = false;

      this.$bvToast.toast("Loading cancelled by user.", {
        title: "Loading Cancelled",
        variant: "warning",
        solid: true,
        autoHideDelay: 3000,
      });
    },

    exportDashboardCsv() {
      if (!this.canExport || !this.lastSearchContext) {
        this.$bvToast.toast(
          "Run a search with the filter and click Search before exporting.",
          {
            title: "Export unavailable",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
        return;
      }
      try {
        exportDashboardToCsv(this.dataDashboard, this.lastSearchContext);
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not generate the CSV file.", {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      }
    },

    exportDashboardExcel() {
      if (!this.canExport || !this.lastSearchContext) {
        this.$bvToast.toast(
          "Run a search with the filter and click Search before exporting.",
          {
            title: "Export unavailable",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
        return;
      }
      try {
        exportDashboardToExcel(this.dataDashboard, this.lastSearchContext);
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not generate the Excel file.", {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      }
    },
  },
};
</script>
